import React, { FC, useState } from 'react';
import classes from './LoginDialog.module.scss';
import CircleLoader from './CircleLoader';
import Button from './Button';
import useGoogle, { googleLogin } from '../login/googleLogin';
import { loginWithApple, loginWithFb, loginWithGoogle, loginWithEmail, loginWithGuest } from '../service/userService';
import WsConnection from '../service/WsConnection';
import { appleLoginMobile, appleLoginWeb } from '../login/appleLogin';
import { fbLogin } from '../login/fbLogin';
import { fbInstantLogin, isFbInstantContext } from '../login/fbInstantLogin';
import { history } from '../reducers/store';

const LoginDialog: FC = () => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [loaderVisible, setLoaderVisible] = useState<boolean>(false);
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [guestNameInput, setGuestNameInput] = useState('');
  const googleAuth = useGoogle();

  const handleFbLogin = async () => {
    try {
      setLoaderVisible(true);
      setErrorMessage(undefined);
      const token = await fbLogin();
      const response = await loginWithFb(token);
      // handle unmounted case
      setLoaderVisible(false);
      console.log('login resp', response);
      WsConnection.connect();
    } catch (err) {
      console.error('Facebook login failed', err);
      // handle unmounted case
      setErrorMessage('Login with Facebook failed.');
      setLoaderVisible(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setLoaderVisible(true);
      setErrorMessage(undefined);
      let token;
      if (!window.runtimeConfig.IS_MOBILE) {
        const googleUser = await googleAuth.signIn();
        token = googleUser?.tokenId;
      } else {
        const googleUser = await googleLogin();
        token = googleUser?.idToken;
      }
      if (!token) return;
      const response = await loginWithGoogle(token);
      setLoaderVisible(false);
      console.log('login resp', response);
      WsConnection.connect();
    } catch (err) {
      console.error('Google login failed', err);
      setErrorMessage('Login with Google failed.');
      setLoaderVisible(false);
    }
  };

  const handleAppleLogin = async () => {
    try {
      setLoaderVisible(true);
      setErrorMessage(undefined);
      let token;
      let userName;
      if (window.runtimeConfig.IS_MOBILE) {
        const appleResponse = await appleLoginMobile();
        token = appleResponse?.identityToken;
        userName = appleResponse.fullName?.givenName || appleResponse.fullName?.familyName || '';
      } else {
        const appleWebResponse = await appleLoginWeb();
        token = appleWebResponse.token;
        userName = appleWebResponse.userName;
      }
      if (!token) return;
      const response = await loginWithApple(token, userName);
      setLoaderVisible(false);
      console.log('login resp', response);
      WsConnection.connect();
    } catch (err) {
      console.error('Apple login failed', err);
      setErrorMessage('Login with Apple failed.');
      setLoaderVisible(false);
    }
  };

  const handleEmailLogin = async () => {
    if (!emailInput || !passwordInput) {
      setErrorMessage('Please enter your email and password.');
      return;
    }
    try {
      setLoaderVisible(true);
      setErrorMessage(undefined);
      await loginWithEmail(emailInput, passwordInput);
      setLoaderVisible(false);
      WsConnection.connect();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Login failed.';
      setErrorMessage(Array.isArray(msg) ? msg.join(' ') : msg);
      setLoaderVisible(false);
    }
  };

  const handleGuestLogin = async () => {
    try {
      setLoaderVisible(true);
      setErrorMessage(undefined);
      await loginWithGuest(guestNameInput);
      setLoaderVisible(false);
      WsConnection.connect();
    } catch (err) {
      console.error('Guest login failed', err);
      setErrorMessage('Guest login failed.');
      setLoaderVisible(false);
    }
  };

  const handleSignUp = () => {
    history.push('/signup');
  };

  const handleFbInstantLogin = async () => {
    try {
      setLoaderVisible(true);
      setErrorMessage(undefined);
      await fbInstantLogin();
      setLoaderVisible(false);
      WsConnection.connect();
    } catch (err) {
      console.error('FB Instant login failed', err);
      setErrorMessage('Facebook Instant login failed.');
      setLoaderVisible(false);
    }
  };

  return (
    <div className={classes.mainCont}>
      <div className={classes.bg}></div>
      <div className={classes.panel}>
        <div className={classes.header}>
          <div className={classes.title}>Login</div>
        </div>
        <div className={classes.body}>
          <form
            className={classes.emailForm}
            onSubmit={(e) => {
              e.preventDefault();
              handleGuestLogin();
            }}>
            <input
              className={classes.input}
              type="text"
              placeholder="Name (optional)"
              maxLength={24}
              value={guestNameInput}
              onChange={(e) => setGuestNameInput(e.target.value)}
            />
            <Button onClick={handleGuestLogin} disabled={loaderVisible}>
              Play as Guest
            </Button>
          </form>
        </div>
        <div className={classes.divider}>or</div>
        <div className={classes.btnBar}>
          {isFbInstantContext() ? (
            <Button onClick={handleFbInstantLogin}>Play via Facebook</Button>
          ) : (
            <>
              <Button onClick={handleFbLogin}>Facebook</Button>
              <Button onClick={handleGoogleLogin}>Google</Button>
              <Button onClick={handleAppleLogin}>Apple</Button>
            </>
          )}
        </div>
        <div className={classes.divider}>or</div>
        <div className={classes.body}>
          {loaderVisible && <CircleLoader size={20} />}
          {errorMessage && <div className={classes.error}>{errorMessage}</div>}
          <form
            className={classes.emailForm}
            onSubmit={(e) => {
              e.preventDefault();
              handleEmailLogin();
            }}>
            <input
              className={classes.input}
              type="email"
              autoComplete="email"
              placeholder="Email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
            />
            <input
              className={classes.input}
              type="password"
              autoComplete="current-password"
              placeholder="Password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
            />
            <Button onClick={handleEmailLogin} disabled={loaderVisible}>
              Login
            </Button>
          </form>
          <div className={classes.signUpRow}>
            No account?{' '}
            <span className={classes.signUpLink} onClick={handleSignUp}>
              Sign up
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginDialog;
